/* tslint:disable:max-line-length */
import {Injectable, Input, OnDestroy} from '@angular/core';
import {MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {ConfigService} from '@docma-mm-core/config/config.service';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {BasedataService} from '@docma-mm-core/shared/basedata.service';
import {Project} from '@docma-mm-core/project/project';
import {BehaviorSubject} from 'rxjs';
import {ReportDialogComponent} from './dialog/report-dialog.component';
import {StepperService} from '@mydocma/import-export';
import {StepNextMethodEnum} from '../faults-writing/faults-writing-stepper/steps/step-next-method.enum';
import {HttpClient} from '@angular/common/http';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {TranslateService} from '@ngx-translate/core';
import {MmReportTypeEnum} from '../../mm-report-type.enum';
import {MmFaultsReportingService} from '../mm-faults-reporting.service';
import {LayoutStateModel} from '../../../mm-filter/layout/layout-state.model';
import {MmLayoutStateService} from '../../../mm-filter/layout/layout-state.service';
import {FaultReportConfig} from './faults-report.config';
import {ReportTemplateData} from '@docma-mm-core/report-template/report-template';
import {ReportingModeEnum} from './common/reporting-mode.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Task, TaskFileDownloadService, TaskService, TaskStatusEnum} from '@mydocma/task';
import {MmModel} from '@docma-mm-core/model/mm.model';
import {MmReportPreview} from './common/report-preview';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {MediaQueryService} from '@mydocma/core';
import {LlOptionEnum} from '../../ll-option.enum';
import {LayoutField} from '@docma-mm-core/layout/layout-field';
import {ColDefsService} from '../../../mm-faults/mm-faults-ag-grid/col-defs/col-defs.service';
import {SettingNamesEnum} from '../../../mydocma-settings/setting-names.enum';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {SettingsService} from '../../../mydocma-settings/settings.service';
import {colTypeMinWidth} from '../../../mm-faults/mm-faults-ag-grid/col-defs/field-defaults';
import {MmReportTask} from '../../mm-report-task';
import { ConnectivityService } from '@mydocma/core';
import { PromptDialogService } from '@mydocma/component-library/src/lib/prompt-dialog';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
@UntilDestroy()
export class ReportService extends BasedataService implements OnDestroy {

  private dialogRef: MatDialogRef<any>;

  public loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  nextSubject: BehaviorSubject<StepNextMethodEnum> = new BehaviorSubject<StepNextMethodEnum>(null);
  next$ = this.nextSubject.asObservable();

  private reportConfig: FaultReportConfig;


  @Input() stepperService: StepperService;
  @Input() emailButtonVisible = false;


  selectedTemplateSubject = new BehaviorSubject<ReportTemplateData>(null);
  selectedTemplate$ = this.selectedTemplateSubject.asObservable();

  private reportPendingState = new Map<string, BehaviorSubject<boolean>>();
  private reportStartingState = new BehaviorSubject<boolean>(false);
  layoutState: LayoutStateModel;

  private currentTask: Task;
  private downloadedTaskIds: string[] = [];

  constructor(protected configService: ConfigService,
              private readonly translateService: TranslateService,
              private readonly notificationService: NotificationService,
              private readonly mmReportingService: MmFaultsReportingService,
              private readonly layoutStateService: MmLayoutStateService,
              private taskDownloadService: TaskFileDownloadService,
              private readonly taskService: TaskService,
              public readonly mediaQueryService: MediaQueryService,
              private readonly colDefsService: ColDefsService,
              private readonly settingService: SettingsService,
              private http: HttpClient,
              public dialog: MatDialog,
              private readonly connectivityService: ConnectivityService,
              private readonly dialogService: PromptDialogService,
              private readonly logger: Logger) {

    super(configService);

    this.layoutStateService.currentLayoutState$.subscribe(p => {
      this.layoutState = p.state;
    });


    mmReportingService.onReportFileDownloaded
      .pipe(untilDestroyed(this))
      .subscribe(async res => {
        if (this.currentTask) {
          if (res.task?.id === this.currentTask.id) {
            this.reportConfig.previewModel.data.blob = res.file;
          }
        }
      });

    mmReportingService.onReportTaskFinished
      .pipe(untilDestroyed(this))
      .subscribe(async task => {
        if (this.currentTask) {
          if (task.id === this.currentTask.id) {
            await this.onTaskChanged(task);
          }
        }
      });
  }


  onTaskChanged(task: Task) {
    this.currentTask = task;
    const status = task.status.value;
    if (status === TaskStatusEnum.ended) {
        this.reportConfig.previewModel.loadState.status = LoadStatusEnum.Loaded;
        this.loadingSubject.next(false);
        this.generatePreviewFile(this.currentTask);
    } else if (status === TaskStatusEnum.failed || status === TaskStatusEnum.canceled) {
        this.reportConfig.previewModel.loadState.status = LoadStatusEnum.Error;
        this.loadingSubject.next(false);
        this.logger.error(this.translateService.instant('MM.reporting.reportGenError'), '', 'ReportService');
        this.notificationService.error(this.translateService.instant('MM.reporting.reportGenError'));
    }
  }

  ngOnDestroy(): void {
    console.log('reports.service.destroy!!');
  }

  async cancelReportConfiguration(reportConfig: FaultReportConfig) {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    }
  }

  async closeReportConfiguration(reportConfig: FaultReportConfig) {
    if (this.dialogRef) {
      this.dialogRef.close(true);
    }
  }

  startReportConfiguration(project: Project, faults: Array<FaultData>) {
    try {
      if (this.connectivityService.isOfflineVal) {
        this.dialogService.showOfflineNotificationDialog();
        return;
      }

      this.notifyLoadingState(false);
      this.selectedTemplateSubject.next(null);
      const reportConfig: FaultReportConfig = {
        project,
        faults,
        layoutState: this.layoutState
      };

      this.dialogRef = this.dialog.open(ReportDialogComponent, {
        data: {
          config: reportConfig,
          service: this
        },
        panelClass: ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-mm-writing-dialog', 'mydocma-mm-report-dialog'],
        autoFocus: false,
        disableClose: true
      });
    } catch (e) {
    }
  }

  public notifyLoadingState(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  async createReport(config: FaultReportConfig) {
    await this.closeReportConfiguration(config);

    try {
      this.reportStartingState.next(true);

      let url: string;
      let body: any;
      let reportType: MmReportTypeEnum;

      if (config.selectedTemplate.DataSourceQualifiedName == 'Defectlist') {
        url = super.getFullUrl('/reporting/defectlist');
        body = await this.getDefectListReportingParams(config, ReportingModeEnum.Print);
        reportType = MmReportTypeEnum.faultsList;
      }
      else {
        url = super.getFullUrl(`/reporting/report/${config.selectedTemplate.Id}/print`);
        body = await this.getReportReportingParams(config, ReportingModeEnum.Print);
        reportType = MmReportTypeEnum.report;
      }

      const task = await this.http.post<any>(url, JSON.stringify(body), { headers: this.getHeaders() }).toPromise();
      this.mmReportingService.registerReportTask({
        projectId: config.project.MyDocmaUid,
        taskId : task.TaskId,
        reportType,
        reports: ((task.ExternalReportIds?.length ?? 0) > 0 ? Object.fromEntries(task.ExternalReportIds.map(x => [x, null])) : null)
     } as MmReportTask);

      this.reportStartingState.next(false);
      this.mmReportingService.openMessageCenter();
      return task;
   } catch (err) {
     this.reportStartingState.next(false);
     this.logger.error(err, '', 'ReportService');
     this.notificationService.error(this.translateService.instant('MM.reporting.reportGenError'));
   }
  }

  private async getDefectListReportingParams(config: FaultReportConfig, mode: ReportingModeEnum) {
    const sorting = (await this.colDefsService.getSortItems()).LayoutSortItems.map(m => ({DefaultName: m.Field.DefaultName, SortDirection: m.Direction, IsGroupSorting: m.IsGroupSorting}));
    const groupColumns: Array<string[]> = sorting.filter(x => !!x.IsGroupSorting).map(x => [x.DefaultName]);

    const layout = this.colDefsService.currentLayoutState.layout;
    const layoutState = await this.layoutStateService.getLayoutStateOfLayout(layout);
    const columnOrder = await this.layoutStateService.getLayoutColumnOrderOfLayout(layout);

    const layoutFieldsByFieldId = layout.Fields
      .reduce((acc: { [id: number]: LayoutField }, curr) => {

        if (curr.Field?.Id && acc[curr.Field.Id] == null) {
          acc[curr.Field.Id] = curr;
        }
        return acc;
      }, {});

    const fieldOptions: any[] = [];
    if (columnOrder) {
      let p = 0;
      for (const colOrder of columnOrder.sort((a, b) => a.Index - b.Index)) {
        const layoutField = (layoutFieldsByFieldId.hasOwnProperty(colOrder.Field.Id) && layoutFieldsByFieldId[colOrder.Field.Id] ? layoutFieldsByFieldId[colOrder.Field.Id] : null);
        if (layoutField && layoutField?.Field?.DefaultName) {
          fieldOptions.push({
            DefaultName: layoutField.Field.DefaultName,
            Position: p,
          });
          p++;
        }
      }
    }

    if (layoutState) {
      for (const layoutStateField of layoutState.Fields) {
        if (layoutFieldsByFieldId.hasOwnProperty(layoutStateField.Field.Id) && layoutFieldsByFieldId[layoutStateField.Field.Id].Width != (layoutStateField.Width * 10)) {
          const found = fieldOptions.find(x => x.DefaultName === layoutFieldsByFieldId[layoutStateField.Field.Id].Field.DefaultName);
          if (!found && layoutFieldsByFieldId[layoutStateField.Field.Id].Field.DefaultName) {
            fieldOptions.push({
              DefaultName: layoutFieldsByFieldId[layoutStateField.Field.Id].Field.DefaultName,
              Position: layoutStateField.SortId ?? layoutFieldsByFieldId[layoutStateField.Field.Id].SortId,
              Width: (layoutStateField.Width * 10)
            });
          }
          else if (found) {
            found.Width = (layoutStateField.Width * 10);
          }
        }
      }
    }

    for (const f of config.layoutState.layout.Fields) {
      const found = fieldOptions.find(x => x.DefaultName === f.Field.DefaultName);
      if (!found && (!f.Width || f.Width === 0)) {
        fieldOptions.push({
          DefaultName: f.Field.DefaultName,
          Position: f.SortId,
          Width: colTypeMinWidth[f.Field.DataType] * 10
        });
      } else if (!!found && (!found.Width || found.Width === 0)) {
        found.Width = colTypeMinWidth[f.Field.DataType] * 10;
      }
    }

    const useAlternativeReportingApi = (await this.settingService.loadGlobal(PersonalizationKeysEnum.UseAlternativeReportingApi, SettingNamesEnum.UseAlternativeReportingApi, false) ?? false);

    let printerless: any;
    if (this.configService.getReportingConfig().printerless !== null) {
      printerless = this.configService.getReportingConfig().printerless === false ? 0 : 1;
    } else {
      printerless = useAlternativeReportingApi ? 0 : 1;
    }

    const llOptions = [{llOption: LlOptionEnum.Printerless, value: printerless}];

    if (!!this.configService.getReportingConfig().rtfShareObjectsThreshold) {
      llOptions.push({llOption: LlOptionEnum.RtfShareObjectsThreshold, value: (this.configService.getReportingConfig().rtfShareObjectsThreshold ?? 50)});
    }

    if (!!this.configService.getReportingConfig().virtualDeviceScalingOptions) {
      llOptions.push({llOption: LlOptionEnum.VirtualDeviceScalingOptions, value: (this.configService.getReportingConfig().virtualDeviceScalingOptions ?? 600)});
    }


    return  {
      Mode: mode,  /* Preview = 0, PreviewSingle = 1, Print = 2, Send = 3 */
      ReportName: config.options.headline,
      IsProjectNumberVisible: config.options.showProjectNumber,
      IsProjectNameVisible: config.options.showProjectTitle,
      LayoutId: config.layoutState.layout.Id,
      IsLayoutFilterDisabled: config.layoutState.layout.FilterDisabled,
      FooterLeft: config.options.footerLeft,
      FooterMiddle: config.options.footerRight,
      IsDateVisible: config.options.showFooterDate,
      IsVisible_Creator: config.options.showFooterUser,
      FaultIds: config.faults.map(p => p.Id),
      PaperOrientation: config.options.orientation,     /* Portrait = 1, Landscape = 2 */
      PaperName: config.options.paperFormat,
      Sorting: sorting,
      GroupColumns: groupColumns,
      FieldOptions: fieldOptions,
      HighResolutionImages: config.options.printResolution,
      AutoSizeColumns: config.options.columnSize,
      LlOptions: llOptions,
      // LlExportOptions: [
      //   /* NOT EXISTING IN NEWER LL VERSION... */
      //   // {"llExportOption": LlExportOptionEnum.PdfFontMode, "value": "5"},
      //   // {"llExportOption": LlExportOptionEnum.PdfPdfAMode, "value": "0"},
      // ],
      IsLogEnabled: (config.options.EnableLlLog ? '{38098f65-1113-4414-8429-37929cedb2b5}' : null),
      UseAlternativeReportingApi: useAlternativeReportingApi
    };
  }

  private async getReportReportingParams(config: FaultReportConfig, mode: ReportingModeEnum) {
    const useAlternativeReportingApi = (await this.settingService.loadGlobal(PersonalizationKeysEnum.UseAlternativeReportingApi, SettingNamesEnum.UseAlternativeReportingApi, false) ?? false);

    let printerless: any;
    if (this.configService.getReportingConfig().printerless !== null) {
      printerless = this.configService.getReportingConfig().printerless === false ? 0 : 1;
    } else {
      printerless = useAlternativeReportingApi ? 0 : 1;
    }

    const llOptions = [{llOption: LlOptionEnum.Printerless, value: printerless}];

    if (!!this.configService.getReportingConfig().rtfShareObjectsThreshold) {
      llOptions.push({llOption: LlOptionEnum.RtfShareObjectsThreshold, value: (this.configService.getReportingConfig().rtfShareObjectsThreshold ?? 50)});
    }

    if (!!this.configService.getReportingConfig().virtualDeviceScalingOptions) {
      llOptions.push({llOption: LlOptionEnum.VirtualDeviceScalingOptions, value: (this.configService.getReportingConfig().virtualDeviceScalingOptions ?? 600)});
    }

    return  {
      Header: config.options.headline,
      IsVisible_ProjectName1: config.options.showProjectNumber,
      IsVisible_ProjectName2: config.options.showProjectTitle,
      FooterLeft: config.options.footerLeft,
      FooterRight: config.options.footerRight,
      IsVisible_Creator: config.options.showFooterUser,
      IsVisible_CreatedOn: config.options.showFooterDate,
      IsVisible_ImageDescription: config.options.fileName,
      IsVisible_ImageFileDate: config.options.imageDate,
      IsVisible_ImageFileName: config.options.fileName,
      FaultIds: config.faults.map(p => p.Id),
      PaperOrientation: config.options.orientation,     /* Portrait = 1, Landscape = 2 */
      PaperName: config.options.paperFormat,
      Sorting: null,
      LlOptions: llOptions,
      LlExportOptions: [
        /* NOT EXISTING IN NEWER LL VERSION... */
        // {"llExportOption": LlExportOptionEnum.PdfFontMode, "value": "5"},
        // {"llExportOption": LlExportOptionEnum.PdfPdfAMode, "value": "0"},
      ],
      EnableLlLog: (config.options.EnableLlLog ? '{7340D61E-393C-4C87-BC63-C6B681731799}' : null),
      UseAlternativeReportingApi: useAlternativeReportingApi
    };
  }


  async createPreview(config: FaultReportConfig) {
    this.reportConfig = config;
    let url: string;
    let body: any;
    let reportType: MmReportTypeEnum;

    if (config.selectedTemplate.DataSourceQualifiedName == 'Defectlist') {
      url = super.getFullUrl('/reporting/defectlist');
      body = await this.getDefectListReportingParams(config, ReportingModeEnum.Preivew);
      reportType = MmReportTypeEnum.faultsList;
    }
    else {
      url = super.getFullUrl(`/reporting/report/${config.selectedTemplate.Id}/preview`);
      body = await this.getReportReportingParams(config, ReportingModeEnum.Preivew);
      reportType = MmReportTypeEnum.report;
    }

    const previewModel = new MmModel<MmReportPreview>();
    previewModel.loadState.status = LoadStatusEnum.Loading;
    config.previewModel = previewModel;

    try {
      this.loadingSubject.next(true);

      const currentReportTask = await this.http.post<any>(url, JSON.stringify(body), { headers: this.getHeaders() }).toPromise();
      this.currentTask = {id: currentReportTask.TaskId} as Task;
      previewModel.data.task = this.currentTask;

      this.mmReportingService.registerReportTask({
        projectId: config.project.MyDocmaUid,
        taskId : currentReportTask.TaskId,
        reportType,
        reports: ((currentReportTask.ExternalReportIds?.length ?? 0) > 0 ? Object.fromEntries(currentReportTask.ExternalReportIds.map(x => [x, null])) : null)
      } as MmReportTask);

  } catch (e) {
      this.loadingSubject.next(false);
      this.logger.error(e, '', 'ReportService');
      this.notificationService.error(this.translateService.instant('MM.reporting.previewCreationError'));
      previewModel.loadState.status = LoadStatusEnum.Error;
  }

    return config.previewModel;
}

  public async downloadPreview(config: FaultReportConfig): Promise<void> {
    try {
      await this.taskDownloadService.downloadFile(config.previewModel.data.blob, {open: true, preventBrowserDownload: true});
    } catch (e) {
        this.logger.error(e, '', 'ReportService');
        this.notificationService.error('An error occurred while downloading file preview');
    }
  }

  private async generatePreviewFile(task: Task) {
      try {
      if (!task.statusLog || this.downloadedTaskIds.some(x => x === task.id)) {
        return null;
      }

      // TODO
      let endpoint: string;
      task.statusLog.forEach(s => {
        if (s.file?.url) {
          endpoint = s.file.url;
        }
      });
      if (!endpoint) {
        return null;
      }

      this.downloadedTaskIds.push(task.id);

      // const isIos = this.mmReportingService.isIOSDevice();
      // const isFirefox = window.navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
      const file = await this.taskDownloadService.downloadFile(endpoint, {
        open: false,
        preventBrowserDownload: true
      });
      if (file) {
        this.reportConfig.previewModel.data.blob = file;
      }
    } catch (e) {
        this.logger.error(e, '', 'ReportService');
        this.notificationService.error('An error occurred while generating while preview');
    }
}

}
